import React, {
  useContext, useEffect, useMemo, useState, memo, useRef
} from 'react';
import classNames from 'classnames';
import { string, func, number } from 'prop-types';
import {
  arrayOf,
  customType,
  extend,
  params,
  shape as shapeType,
  string as stringType,
  number as numberType,
  bool,
  useLazyDataModel,
  QueryProvider,
  hoist
} from '@thd-nucleus/data-sources';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { Carousel } from '@one-thd/sui-carousel';
import { AddTo } from '@thd-olt-component-react/add-to';
import {
  Button, Skeleton, SkeletonContent, SkeletonLine, SkeletonBlock
} from '@one-thd/sui-atomic-components';
import { ListRecsPod } from './ListRecsPod';
import { ProductListContext } from '../ProductListContext';
import { INTENT_SEARCH_EVENT } from '../productListHelper';

const HoistedCarousel = hoist(Carousel, ListRecsPod);

const isIntentRecsEqual = (prevProps, nextProps) => {
  return (
    prevProps.keyword === nextProps.keyword
    && prevProps.quantity === nextProps.quantity
    && prevProps.onIntentResolved === nextProps.onIntentResolved
  );
};

const ListRecs = memo(({ keyword, onIntentResolved, quantity }) => {
  const { storeId, membershipInformation, isLocalized, storeZip } = useStore({ varnish: true });
  const { list } = useContext(ProductListContext);
  const THDCustomer = useThdCustomer();
  const { svocID = '' } = THDCustomer || {};
  const [nValueMap, setNValueMap] = useState({});
  const [keywordMap, setKeywordMap] = useState({});
  const [productsLoaded, setProductsLoaded] = useState([]);
  const { channel, deliveryZip: serverDeliveryZip } = useContext(ExperienceContext);

  const additionalSearchParams = useMemo(() => {
    const additionalSearchData = {
      deliveryZip: serverDeliveryZip,
      svocID,
      sponsored: false
    };
    if (!isLocalized) return additionalSearchData;
    if (typeof window !== 'undefined' && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails) {
      const deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
      additionalSearchData.deliveryZip = deliveryZipCode?.zipcode;
    }
    return additionalSearchData;
  }, [serverDeliveryZip, isLocalized]);

  // Debounced keyword state
  const [debouncedKeyword, setDebouncedKeyword] = useState(keyword);

  const loyaltyMembershipInput = useMemo(() => {
    return membershipInformation?.data?.loyaltyMembership || null;
  }, [membershipInformation?.data?.loyaltyMembership]);

  // Debounce the keyword with 1 second delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(keyword);
    }, 1000);

    // Cleanup function to cancel the timer if keyword changes before 1 second
    return () => {
      clearTimeout(timer);
    };
  }, [keyword]);

  const [findProducts, { data, loading, error }] = useLazyDataModel('searchModel', {
    variables: {
      storeId,
      startIndex: 0,
      pageSize: 5,
      channel: channel.toUpperCase(),
      orderBy: { field: 'BEST_MATCH', order: 'ASC' },
      loyaltyMembershipInput,
      additionalSearchParams
    }
  });

  const { metadata, products } = data?.searchModel || {};

  useEffect(() => {
    if (debouncedKeyword?.length > 1) {
      setProductsLoaded([]);
      const redirectedKeyword = keywordMap[debouncedKeyword] || debouncedKeyword;
      findProducts({
        variables: {
          keyword: redirectedKeyword,
          additionalSearchParams
        }
      });
    }
  }, [debouncedKeyword, additionalSearchParams]);

  const getNvalue = (path) => {
    const pathArr = path.split('/');
    const fullNValue = pathArr.find((part) => part.startsWith('N-') || part.startsWith('Ntt-'));
    if (fullNValue) {
      const nValue = fullNValue.split('?')?.[0];
      if (nValue.startsWith('N-')) {
        return nValue.slice(2);
      }
      return nValue.slice(4);
    }
    return null;
  };

  const getNewKeyword = (path) => {
    const pathArr = path.split('/');
    const indexOfs = pathArr.findIndex((part) => part === 's');
    const keywordPart = pathArr?.[indexOfs + 1];
    if (keywordPart) {
      const keywordArr = keywordPart.split('?');
      return keywordArr?.[0] || null;
    }
    return null;
  };

  useEffect(() => {
    if (products?.length) {
      setProductsLoaded(products.slice(0, 5));
    } else {
      const { searchRedirect } = metadata || {};
      if (searchRedirect) {
        if (searchRedirect.startsWith('/b/')) {
          const nValue = getNvalue(searchRedirect);
          if (nValue) {
            setNValueMap({ ...nValueMap, [keyword]: nValue });
          }
        } else if (searchRedirect.startsWith('/s/')) {
          const newKeyword = getNewKeyword(searchRedirect);
          if (newKeyword) {
            setKeywordMap({ ...keywordMap, [keyword]: newKeyword });
          }
        }
      }
    }
  }, [products, loading]);

  // Re trigger the searchNav call with nValue if a keyword is associated with an nValue (eg: 'sofa')
  // In PLP pages users will be redirected to browse page for this scenario.
  useEffect(() => {
    if (keyword?.length) {
      let nValue = null;
      if (nValueMap[keyword]) {
        nValue = nValueMap[keyword];
        if (nValue) {
          setProductsLoaded([]);
          findProducts({
            variables: {
              navParam: nValue,
              additionalSearchParams
            }
          });
        }
      }
    }
  }, [keyword, nValueMap]);

  useEffect(() => {
    if (keyword?.length) {
      if (keywordMap[keyword]) {
        const newKeyword = keywordMap[keyword];
        setProductsLoaded([]);
        findProducts({
          variables: {
            keyword: newKeyword,
            additionalSearchParams
          }
        });
      }
    }
  }, [keyword, keywordMap]);

  const productsToShow = useMemo(() => {
    return [...productsLoaded, { type: 'view-more' }];
  }, [productsLoaded]);

  if (!keyword || error) {
    return null;
  }

  const noProducts = productsLoaded?.length === 0;

  // Show loading state while data is being fetched
  const showLoading = loading && !products?.length;

  const viewMoreClass = classNames(
    'sui-flex sui-pt-20',
    {
      'sui-hidden': noProducts
    }
  );

  const breakpoints = {
    sm: {
      slidesPerView: 1,
      slidesPerGroup: 1
    },
    md: {
      slidesPerView: 2,
      slidesPerGroup: 2
    },
    lg: {
      slidesPerView: 3,
      slidesPerGroup: 3
    },
    xl: {
      slidesPerView: 3,
      slidesPerGroup: 3
    },
    '2xl': {
      slidesPerView: 4,
      slidesPerGroup: 4
    }
  };

  const wrapperClass = classNames(
    'sui-bg-subtle',
    {
      'sui-pt-2 sui-pl-3 sui-mt-2': !(!showLoading && noProducts),
    }
  );

  return (
    <div
      data-component="ListRecs"
      id="list-recs-carousel"
      className={wrapperClass}
    >
      <QueryProvider
        cacheKey="list-pods"
        dataSource="searchNav"
        dataSources="searchNav"
        defaultVariables={{
          storeId,
          isBrandPricingPolicyCompliant: true,
          loyaltyMembershipInput,
          zipCode: storeZip
        }}
      >
        <HoistedCarousel
          spaceBetween={10}
          slidesPerView={1}
          slidesPerGroup={1}
          containerId="list-recs-carousel"
          breakpoints={breakpoints}
          disableMargin
          layout="full-bleed"
          peek="sm"
        >
          {showLoading ? (
            Array.from({ length: 5 }, (item, index) => (
              <div className="sui-w-full" key={`intent-rec-placeholder${index}`}>
                <Skeleton
                  key={index}
                  orientation="vertical"
                  grow
                  density="loose"
                >
                  <SkeletonContent disablePadding orientation="horizontal" density="loose">
                    <SkeletonContent density="loose" disablePadding initialSize="2/4">
                      <SkeletonBlock aspect="square" />
                    </SkeletonContent>
                    <SkeletonContent disablePadding density="loose" initialSize="2/4">
                      <SkeletonLine fullWidth numberOfLines={3} />
                    </SkeletonContent>
                  </SkeletonContent>
                  <SkeletonContent disablePadding grow density="loose">
                    <SkeletonLine fullWidth variant="single" />
                  </SkeletonContent>
                </Skeleton>
              </div>
            ))
          ) : (
            productsToShow?.map((product, index) => {
              if (product.type === 'view-more') {
                return (
                  <QueryProvider
                    key="view-more"
                    cacheKey="list-add-to-materials"
                    defaultVariables={{
                      storeId,
                      isBrandPricingPolicyCompliant: true,
                      loyaltyMembershipInput,
                    }}
                  >
                    <div className={viewMoreClass}>
                      <AddTo
                        addToType="List"
                        list={{
                          listId: list.id
                        }}
                        products={[]}
                        productPodItemData={{ searchKeyword: keyword, quantity, onIntentResolved }}
                        CustomAddToActionElement={({ onClick }) => (
                          <Button
                            aria-label="intent-pod-search-button"
                            data-testid="intent-pod-search-button"
                            onClick={(event) => {
                              LIFE_CYCLE_EVENT_BUS.trigger(INTENT_SEARCH_EVENT, {
                                intentText: keyword,
                                action: 'view more'
                              });
                              onClick(event);
                            }}
                            variant="text"
                          >
                            View More
                          </Button>
                        )}
                      />
                    </div>
                  </QueryProvider>
                );
              }
              return (
                <ListRecsPod
                  key={product.itemId || index}
                  itemId={product.itemId}
                  storeId={storeId}
                  podIndex={index}
                  showATQ={false}
                  showStoreSku
                  sortableRef={null}
                  handleQuantityUpdate={() => {}}
                  onIntentResolved={onIntentResolved}
                />
              );
            })
          )}
        </HoistedCarousel>
      </QueryProvider>
    </div>
  );
}, isIntentRecsEqual);

ListRecs.displayName = 'ListRecs';

ListRecs.propTypes = {
  keyword: string.isRequired,
  onIntentResolved: func.isRequired,
  quantity: number
};

ListRecs.defaultProps = {
  quantity: 1
};

ListRecs.dataModel = extend({
  searchModel: params({
    keyword: stringType(),
    navParam: stringType(),
    storeId: stringType(),
    storefilter: customType('StoreFilter').enum(['ALL', 'IN_STORE', 'ONLINE'], 'ALL'),
    loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
      svocID: stringType(),
      programTiers: arrayOf(
        shapeType({
          tier: stringType(),
          program: stringType()
        })
      )
    }),
    additionalSearchParams: customType('AdditionalParams').shape({
      deliveryZip: stringType(),
      sponsored: bool(),
      svocID: stringType()
    }),
    channel: customType('Channel').enum(['DESKTOP', 'MOBILE', 'TABLET'], 'DESKTOP')
  }).shape({
    metadata: shapeType({
      searchRedirect: stringType(),
      analytics: shapeType({
        semanticTokens: stringType(),
      })
    }),
    products: params({
      startIndex: numberType(),
      pageSize: numberType(),
      orderBy: customType('ProductSort').shape({
        field: customType('ProductSortField').enum([
          'TOP_SELLERS', 'MOST_POPULAR', 'TOP_RATED', 'NEW_ITEMS',
          'BEST_MATCH', 'PRODUCT_NAME', 'BRAND_NAME', 'PRICE'
        ], 'BEST_MATCH'),
        order: customType('SortOrder').enum(['ASC', 'DESC'], 'ASC')
      })
    })
      .arrayOf(ListRecsPod.dataModel.product),
  })
});

export { ListRecs };
